import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/**
 * Portrait PDP assets use `aspect-ratio` on `.product-media`; lazy images may never load inside `<dialog>`.
 * Force eager decode + slideshow `[in-viewport]` after morph / open.
 *
 * @param {Element | null} modalContent - `#quick-add-modal-content`
 */
function primeQuickAddGalleryMedia(modalContent) {
  const mediaScope = modalContent?.querySelector('.product-information__media');
  if (!mediaScope) return;

  for (const img of mediaScope.querySelectorAll('img[loading="lazy"]')) {
    img.loading = 'eager';
  }

  mediaScope.querySelector('slideshow-component')?.setAttribute('in-viewport', '');
}

const SIZE_CHART_DRAWER_ID = 'gsq-size-chart-drawer';
const STASHED_SIZE_CHART_DRAWER_ID = 'gsq-size-chart-drawer--stashed';
const QUICK_ADD_SIZE_CHART_ATTR = 'data-quick-add-size-chart';

/**
 * @param {ParentNode | null} container
 * @returns {Element | null}
 */
function findSizeChartDrawer(container) {
  return container?.querySelector(`#${SIZE_CHART_DRAWER_ID}`) ?? null;
}

/**
 * @returns {HTMLElement | null}
 */
function getQuickAddSizeChartDrawer() {
  const drawer = document.querySelector(`#${SIZE_CHART_DRAWER_ID}[${QUICK_ADD_SIZE_CHART_ATTR}]`);
  return drawer instanceof HTMLElement ? drawer : null;
}

function stashExistingSizeChartDrawer() {
  const existing = document.getElementById(SIZE_CHART_DRAWER_ID);
  if (!existing || existing.hasAttribute(QUICK_ADD_SIZE_CHART_ATTR)) return;

  existing.id = STASHED_SIZE_CHART_DRAWER_ID;
  existing.setAttribute('hidden', '');
}

function restoreStashedSizeChartDrawer() {
  const stashed = document.getElementById(STASHED_SIZE_CHART_DRAWER_ID);
  if (!stashed) return;

  stashed.id = SIZE_CHART_DRAWER_ID;
  stashed.removeAttribute('hidden');
  notifySizeChartDrawerMount();
}

function closeQuickAddSizeChartDrawer() {
  const drawer = getQuickAddSizeChartDrawer();
  if (drawer && 'closeDialog' in drawer && typeof drawer.closeDialog === 'function') {
    drawer.closeDialog();
  }
}

function removeQuickAddSizeChartDrawer() {
  closeQuickAddSizeChartDrawer();
  getQuickAddSizeChartDrawer()?.remove();
  restoreStashedSizeChartDrawer();
}

function notifySizeChartDrawerMount() {
  document.dispatchEvent(new CustomEvent('gsq:size-chart-drawer:mount'));
}

/**
 * @param {Element} drawer
 * @returns {Element}
 */
function mountQuickAddSizeChartDrawer(drawer) {
  removeQuickAddSizeChartDrawer();
  stashExistingSizeChartDrawer();

  const mounted = /** @type {Element} */ (drawer.cloneNode(true));
  mounted.querySelectorAll('script').forEach((script) => script.remove());
  mounted.setAttribute(QUICK_ADD_SIZE_CHART_ATTR, '');
  document.body.appendChild(mounted);
  notifySizeChartDrawerMount();

  return mounted;
}

/**
 * @param {Element} modalContent
 * @param {Element | null} mountedDrawer
 */
function syncQuickAddSizeChartTrigger(modalContent, mountedDrawer) {
  const hasChart =
    mountedDrawer?.querySelector('.app-size-chart.schart, .app-size-chart-country-root') != null;

  modalContent.querySelectorAll('.variant-option--has-size-chart').forEach((fieldset) => {
    if (!hasChart) {
      fieldset.classList.remove('variant-option--has-size-chart');
    }
  });

  modalContent.querySelectorAll('.variant-option__size-chart-trigger').forEach((trigger) => {
    trigger.hidden = !hasChart;
  });
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (productLink?.href) {
      return this.#buildProductPageUrl(productLink.href);
    }

    const dataUrl = this.dataset.productUrl;
    if (dataUrl) {
      return this.#buildProductPageUrl(dataUrl);
    }

    return '';
  }

  /**
   * @param {string} href
   * @returns {string}
   */
  #buildProductPageUrl(href) {
    const url = new URL(href, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });

    const slidesScroller = dialogComponent.querySelector(
      '.quick-add-modal__content .product-information__media slideshow-slides'
    );
    slidesScroller?.scrollTo({ left: 0, top: 0, behavior: 'instant' });

    /** SlideshowViewportObserver can lag inside `<dialog>`; base.css gates overflow on `[in-viewport]`. */
    const slideshow = dialogComponent.querySelector(
      '.quick-add-modal__content .product-information__media slideshow-component'
    );
    slideshow?.setAttribute('in-viewport', '');

    const modalContent = document.getElementById('quick-add-modal-content');
    primeQuickAddGalleryMedia(modalContent);

    const picker = modalContent?.querySelector('variant-picker');
    if (picker instanceof VariantPicker) {
      requestAnimationFrame(() => picker.updateVariantPickerCss());
    }
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    /**
     * Flatten product grid to match quick-add layout (title → gallery → variants → buy).
     * Linked color swatch blocks are removed from the clone — they are not shown in quick add.
     */
    const productDetails = productGrid.querySelector('.product-details');
    const media = productGrid.querySelector('.product-information__media');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const sizeChartDrawer = findSizeChartDrawer(productGrid);
    const productPrice = productGrid.querySelector('product-price');
    const titleAnchor = productGrid.querySelector('.view-product-title a');
    const linkedColorBlocks = productDetails
      ? /** @type {Element[]} */ ([...productDetails.querySelectorAll('.linked-color-swatches-block')])
      : [];

    const productTitle = document.createElement('a');
    if (titleAnchor) {
      productTitle.textContent = titleAnchor.textContent?.trim() || '';
      productTitle.href = titleAnchor.href;
    } else {
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;
    }

    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');
    productHeader.appendChild(productTitle);
    if (productPrice) {
      /*
       * PDP `product-price` keeps a `.installments` node in the tree (often display:none until Shop Pay loads).
       * It still counts as a following sibling, so `.text-block.h* > *` + `:last-child` rules leave a large
       * `margin-block-end` on `[ref="priceContainer"]` — reads as empty height inside `.product-header`.
       */
      productPrice.querySelector('.installments')?.remove();
      productHeader.appendChild(productPrice);
    }

    linkedColorBlocks.forEach((block) => block.remove());
    if (variantPicker) variantPicker.remove();
    if (productFormComponent) productFormComponent.remove();
    if (media) media.remove();

    while (productGrid.firstChild) {
      productGrid.removeChild(productGrid.firstChild);
    }

    const variantsShell = document.createElement('div');
    variantsShell.className = 'quick-add-modal__variants-shell';

    productGrid.appendChild(productHeader);
    if (media) productGrid.appendChild(media);
    if (variantPicker) variantsShell.appendChild(variantPicker);
    if (variantsShell.childElementCount > 0) {
      productGrid.appendChild(variantsShell);
    }
    if (productFormComponent) productGrid.appendChild(productFormComponent);

    morph(modalContent, productGrid);

    const mountedSizeChartDrawer = sizeChartDrawer ? mountQuickAddSizeChartDrawer(sizeChartDrawer) : null;
    syncQuickAddSizeChartTrigger(modalContent, mountedSizeChartDrawer);

    this.#syncVariantSelection(modalContent);
    primeQuickAddGalleryMedia(modalContent);
    queueMicrotask(() => primeQuickAddGalleryMedia(modalContent));
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.classList.contains('quick-add--inline')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    removeQuickAddSizeChartDrawer();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
